import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Building2,
  AlertTriangle,
  ArrowRight,
  MapPin,
  RefreshCw,
  AlertCircle,
  Info,
} from "lucide-react";

const ResetBranchStock = () => {
  const [branches, setBranches] = useState([]);
  const { requestWithAuth } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [resetting, setResetting] = useState(false);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    }
  };

  const resetStock = async (e) => {
    e.preventDefault();
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to reset the stock of this branch?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setResetting(true);
          const body = JSON.stringify({
            branch: selectedBranch,
          });

          const data = await requestWithAuth(
            `/inventory/reset-stock/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "The stock of this branch has been reset successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          if (error.response?.data?.error) {
            for (const property in error.response.data.error) {
              toast.error(
                `${property} error: ${error.response.data.error[property]}`
              );
            }
          }
          if (error.response?.status === 500) {
            toast.error("Something went wrong!");
          }
        } finally {
          setResetting(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Reset Branch Stock
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <RefreshCw size={32} className="text-red-600" />
                Reset Branch Stock
              </h1>
            </div>
          </div>
        </div>

        {/* Main Content - Centered Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Building2 className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Select Branch
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Choose a branch to reset its inventory stock
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Warning Banner */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle size={18} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-900">
                        Warning: This action cannot be undone
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Resetting branch stock will permanently delete all
                        inventory records for the selected branch
                      </p>
                    </div>
                  </div>
                </div>

                {/* Branch Selection */}
                <div>
                  <label
                    htmlFor="branch-select"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <MapPin size={16} className="text-gray-500" />
                    Branch Name
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="branch-select"
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    value={selectedBranch || ""}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white transition-all"
                  >
                    <option value="">Select a branch...</option>
                    {branches.map((branch, index) => (
                      <React.Fragment key={index}>
                        {!branch?.is_head_office && (
                          <option value={branch.id}>{branch.name}</option>
                        )}
                      </React.Fragment>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Select the branch whose stock you want to reset
                  </p>
                </div>

                {/* Confirmation Info */}
                {selectedBranch && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertCircle size={18} className="text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-orange-900">
                          Ready to reset
                        </p>
                        <p className="text-xs text-orange-700 mt-1">
                          You will be asked to confirm this action before the
                          stock is reset
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-end">
                  {resetting ? (
                    <button
                      type="button"
                      disabled
                      className="bg-red-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Resetting...
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={resetStock}
                      disabled={!selectedBranch}
                      className={`rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2 ${
                        selectedBranch
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      Reset Stock
                      <ArrowRight size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              What happens when you reset branch stock?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle size={16} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Permanent Deletion
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    All inventory records will be permanently removed
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <RefreshCw size={16} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Fresh Start
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Branch will start with zero inventory stock
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 size={16} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Branch Specific
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Only affects the selected branch location
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Info size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Confirmation Required
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    You'll need to confirm before any changes are made
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetBranchStock;
